import React, { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import {
    FiMessageSquare, FiSend, FiClock, FiCheckCircle,
    FiAlertCircle, FiPlus, FiX, FiPaperclip, FiImage, FiFile,
    FiFilter, FiEdit2, FiSearch, FiRefreshCw,
    FiInbox, FiTag, FiUser, FiBriefcase, FiHash
} from 'react-icons/fi';
import API_BASE_URL, { API_ENDPOINTS } from './config';
import logger from './utils/logger';
import SigningLoader from './SigningLoader';
import { useTheme } from './ThemeContext';
import './Support.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatRelativeTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatTimestamp = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const getInitials = (name = '') => {
    if (!name) return '?';
    return name.split(/[\s@._-]/).filter(Boolean).slice(0, 2).map(n => n[0].toUpperCase()).join('');
};

const PRIORITY_CONFIG = {
    P0: { label: 'Critical', color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
    P1: { label: 'High',     color: '#f97316', bg: 'rgba(249,115,22,0.12)' },
    P2: { label: 'Medium',   color: '#FFD600', bg: 'rgba(255,214,0,0.12)'  },
    P3: { label: 'Low',      color: '#10b981', bg: 'rgba(16,185,129,0.12)' },
    P4: { label: 'Very Low', color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
    P5: { label: 'Minimal',  color: '#9ca3af', bg: 'rgba(156,163,175,0.12)' },
};

const STATUS_CONFIG = {
    open:     { label: 'Open',     color: '#FFD600', icon: <FiClock size={11} /> },
    resolved: { label: 'Resolved', color: '#10b981', icon: <FiCheckCircle size={11} /> },
    closed:   { label: 'Closed',   color: '#6b7280', icon: <FiX size={11} /> },
};

const ATTACHMENT_ACCEPT = 'image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip';
const MAX_MSG_LENGTH = 2000;

// ── Component ─────────────────────────────────────────────────────────────────

const SupportHub = ({ selectedAdminCompany, onSupportUnread }) => {
    const { theme } = useTheme();
    const [tickets, setTickets]               = useState([]);
    const [categories, setCategories]         = useState(['Signing', 'Templates', 'Request Creation', 'Other']);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [activeMessages, setActiveMessages] = useState([]);
    const [messageInput, setMessageInput]     = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [isTicketsLoading, setIsTicketsLoading] = useState(false);
    // unread tracking: { [ticket_id]: count }
    const [unreadCounts, setUnreadCounts]     = useState({});

    // Filters
    const [priorityFilter, setPriorityFilter]         = useState('');
    const [internalPriorityFilter, setInternalPriorityFilter] = useState('');
    const [statusFilter, setStatusFilter]             = useState('');
    const [searchQuery, setSearchQuery]               = useState('');

    // Create Ticket
    const [newTicket, setNewTicket] = useState({
        subject: 'Signing',
        customSubject: '',
        priority: 'P2',
        message: '',
        attachments: [],
    });
    const [isCreating, setIsCreating]   = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Internal Priority Edit
    const [isEditingInternal, setIsEditingInternal]     = useState(false);
    const [tempInternalPriority, setTempInternalPriority] = useState('');

    // Status Update
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

    // Chat Attachments
    const [chatAttachments, setChatAttachments] = useState([]);

    const fileInputRef        = useRef(null);
    const modalFileInputRef   = useRef(null);
    const messagesEndRef      = useRef(null);
    const selectedTicketRef   = useRef(null);
    const socketRef           = useRef(null);

    const userId    = localStorage.getItem('userId');
    const userEmail = localStorage.getItem('email') || localStorage.getItem('userEmail') || '';
    const rolesRaw  = localStorage.getItem('roles') || localStorage.getItem('userRoles') || '[]';
    const userRoles = (() => {
        try {
            return JSON.parse(rolesRaw);
        } catch {
            return [];
        }
    })();
    const companyId = localStorage.getItem('orgId') || localStorage.getItem('companyId');
    const userName  = localStorage.getItem('userName') || userEmail.split('@')[0] || 'User';
    const isSuperAdmin = userRoles.includes('SUPER_ADMIN');

    // ── Scroll ──────────────────────────────────────────────────────────────

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => { scrollToBottom(); }, [activeMessages, scrollToBottom]);

    // ── Fetch Persisted Unread Counts ─────────────────────────────────────
    const fetchUnreadCounts = useCallback(async () => {
        try {
            const params = {};
            // Super admin always sees ALL tickets — not filtered by company selector
            const { data } = await axios.get(`${API_BASE_URL}/tickets/unread-counts`, {
                params,
            });
            const counts = data.unread_counts || {};
            setUnreadCounts(counts);
            const total = Object.values(counts).reduce((a, b) => a + b, 0);
            onSupportUnread?.(total);
        } catch (err) {
            console.error('Failed to fetch unread counts:', err);
        }
    }, [onSupportUnread]);

    // ── Fetch Tickets ────────────────────────────────────────────────────────

    const fetchTickets = useCallback(async () => {
        setIsTicketsLoading(true);
        try {
            const params = {};
            if (priorityFilter)         params.priority          = priorityFilter;
            if (internalPriorityFilter) params.internal_priority = internalPriorityFilter;
            if (statusFilter)           params.status            = statusFilter;
            // Super admin always sees ALL tickets regardless of company selector in dashboard

            const { data } = await axios.get(API_ENDPOINTS.GET_TICKETS || `${API_BASE_URL}/tickets`, {
                params,
            });
            setTickets(data.tickets || []);
        } catch (err) {
            console.error('Failed to fetch tickets:', err);
        } finally {
            setIsTicketsLoading(false);
        }
    }, [priorityFilter, internalPriorityFilter, statusFilter]);

    // ── Fetch Categories ─────────────────────────────────────────────────────

    useEffect(() => {
        axios.get(`${API_BASE_URL}/tickets/categories`)
            .then(({ data }) => setCategories(data.categories || categories))
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Socket.io ────────────────────────────────────────────────────────────

    useEffect(() => {
        fetchTickets().then(() => fetchUnreadCounts());
    }, [fetchTickets, fetchUnreadCounts]);

    useEffect(() => {
        const s = io(API_BASE_URL || "http://localhost:5001");
        socketRef.current = s;
        return () => { s.disconnect(); };
    }, []);

    useEffect(() => {
        const socket = socketRef.current;
        if (!socket) return;
        const joinPayload = {
            user_id:    userId,
            company_id: companyId,
            roles:      userRoles,
            ticket_id:  selectedTicket?.ticket_id,
        };
        socket.emit('join', joinPayload);

        const onNewMessage = (msg) => {
            setActiveMessages(prev => {
                const isDuplicate = prev.some(
                    m => m.timestamp === msg.timestamp && m.sender_id === msg.sender_id && m.text === msg.text
                );
                return isDuplicate ? prev : [...prev, msg];
            });
            // Track unread: message from someone else, not in the active ticket
            if (msg.sender_id !== userId && msg.ticket_id && msg.ticket_id !== selectedTicketRef.current?.ticket_id) {
                setUnreadCounts(prev => {
                    const next = { ...prev, [msg.ticket_id]: (prev[msg.ticket_id] || 0) + 1 };
                    onSupportUnread?.(Object.values(next).reduce((a, b) => a + b, 0));
                    return next;
                });
            }
        };

        const onTicketCreated = (ticket) => {
            setTickets(prev => [ticket, ...prev]);
            // If superadmin receives a new ticket from a user, mark it unread
            if (ticket.user_id !== userId) {
                setUnreadCounts(prev => {
                    const next = { ...prev, [ticket.ticket_id]: 1 };
                    onSupportUnread?.(Object.values(next).reduce((a, b) => a + b, 0));
                    return next;
                });
            }
            if (Notification.permission === 'granted' && ticket.user_id !== userId) {
                new Notification('New Support Ticket', {
                    body: `${ticket.subject} — ${ticket.priority}`,
                });
            }
        };

        const onTicketUpdated = (payload) => {
            setTickets(prev =>
                prev.map(t => t.ticket_id === payload.ticket_id
                    ? { ...t, updated_at: payload.updated_at, last_message: payload.last_message, status: payload.status }
                    : t
                )
            );
        };

        socket.on('new_message', onNewMessage);
        socket.on('ticket_created', onTicketCreated);
        socket.on('ticket_updated', onTicketUpdated);

        return () => {
            socket.off('new_message', onNewMessage);
            socket.off('ticket_created', onTicketCreated);
            socket.off('ticket_updated', onTicketUpdated);
        };
    }, [selectedTicket, userId, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Select Ticket ─────────────────────────────────────────────────────────

    const handleSelectTicket = async (ticket) => {
        setSelectedTicket(ticket);
        selectedTicketRef.current = ticket;
        // Clear unread for this ticket locally
        setUnreadCounts(prev => {
            if (!prev[ticket.ticket_id]) return prev;
            const next = { ...prev };
            delete next[ticket.ticket_id];
            onSupportUnread?.(Object.values(next).reduce((a, b) => a + b, 0));
            return next;
        });
        socketRef.current?.emit('join', { ticket_id: ticket.ticket_id, user_id: userId });
        // Mark as read on backend (persist)
        try {
            axios.patch(`${API_BASE_URL}/tickets/${ticket.ticket_id}/mark-read`, {}).catch(() => {});
            const { data } = await axios.get(`${API_BASE_URL}/tickets/${ticket.ticket_id}`);
            setActiveMessages(data.messages || []);
        } catch (err) {
            console.error('Failed to load messages:', err);
            setActiveMessages(ticket.messages || []);
        }
    };

    // ── File Upload ───────────────────────────────────────────────────────────

    const handleFileUpload = async (e, type) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        setIsUploading(true);
        const uploaded = [];
        try {
            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                const { data } = await axios.post(
                    API_ENDPOINTS.UPLOAD_FILE || `${API_BASE_URL}/upload`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );
                uploaded.push({
                    url: data.url || data.file_url,
                    name: file.name,
                    type: file.type,
                    size: file.size,
                });
            }
            if (type === 'modal') {
                setNewTicket(prev => ({ ...prev, attachments: [...prev.attachments, ...uploaded] }));
            } else {
                setChatAttachments(prev => [...prev, ...uploaded]);
            }
        } catch (err) {
            console.error('Upload failed:', err);
        } finally {
            setIsUploading(false);
            // Reset file input so same file can be re-selected
            e.target.value = '';
        }
    };

    // ── Create Ticket ─────────────────────────────────────────────────────────

    const handleCreateTicket = async (e) => {
        e.preventDefault();
        if (!newTicket.message.trim()) return;
        setIsCreating(true);
        try {
            const payload = {
                subject:        newTicket.subject,
                custom_subject: newTicket.customSubject,
                priority:       newTicket.priority,
                message:        newTicket.message,
                attachments:    newTicket.attachments,
            };
            await axios.post(
                API_ENDPOINTS.CREATE_TICKET || `${API_BASE_URL}/tickets`,
                payload
            );
            setNewTicket({ subject: 'Signing', customSubject: '', priority: 'P2', message: '', attachments: [] });
            setShowCreateModal(false);
        } catch (err) {
            console.error('Failed to create ticket:', err);
        } finally {
            setIsCreating(false);
        }
    };

    // ── Update Internal Priority ──────────────────────────────────────────────

    const handleUpdateInternalPriority = async () => {
        if (!selectedTicket || !tempInternalPriority) return;
        try {
            await axios.patch(
                `${API_BASE_URL}/tickets/${selectedTicket.ticket_id}/internal-priority`,
                { internal_priority: parseInt(tempInternalPriority, 10) }
            );
            setSelectedTicket(prev => ({ ...prev, internal_priority: parseInt(tempInternalPriority, 10) }));
            setTickets(prev =>
                prev.map(t =>
                    t.ticket_id === selectedTicket.ticket_id
                        ? { ...t, internal_priority: parseInt(tempInternalPriority, 10) }
                        : t
                )
            );
        } catch (err) {
            console.error('Failed to update internal priority:', err);
        } finally {
            setIsEditingInternal(false);
        }
    };

    // ── Update Ticket Status ──────────────────────────────────────────────────

    const handleUpdateStatus = async (newStatus) => {
        if (!selectedTicket) return;
        setIsUpdatingStatus(true);
        try {
            await axios.patch(
                `${API_BASE_URL}/tickets/${selectedTicket.ticket_id}/status`,
                { status: newStatus }
            );
            setSelectedTicket(prev => ({ ...prev, status: newStatus }));
            setTickets(prev =>
                prev.map(t =>
                    t.ticket_id === selectedTicket.ticket_id ? { ...t, status: newStatus } : t
                )
            );
        } catch (err) {
            console.error('Failed to update status:', err);
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    // ── Send Message ──────────────────────────────────────────────────────────

    const handleSendMessage = (e) => {
        e.preventDefault();
        const text = messageInput.trim();
        if (!text && chatAttachments.length === 0) return;
        if (!selectedTicket) return;

        const sender_name = isSuperAdmin ? 'Bug Buster' : (userName || userEmail.split('@')[0] || 'User');
        const payload = {
            ticket_id:   selectedTicket.ticket_id,
            text:        text || '',
            sender_id:   userId,
            sender_name,
            sender_role: isSuperAdmin ? 'ADMIN' : 'USER',
            attachments: chatAttachments,
        };
        socketRef.current?.emit('send_message', payload);
        setMessageInput('');
        setChatAttachments([]);
    };

    // ── Filtered Tickets ──────────────────────────────────────────────────────

    const filteredTickets = tickets
        .filter(t => {
            if (!searchQuery) return true;
            const q = searchQuery.toLowerCase();
            return (
                t.subject?.toLowerCase().includes(q) ||
                t.ticket_id?.toLowerCase().includes(q) ||
                t.user_email?.toLowerCase().includes(q)
            );
        })
        .sort((a, b) => {
            const aUnread = unreadCounts[a.ticket_id] || 0;
            const bUnread = unreadCounts[b.ticket_id] || 0;
            if (bUnread !== aUnread) return bUnread - aUnread; // unread first
            return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
        });

    // ── Render Helpers ─────────────────────────────────────────────────────────

    const PriorityBadge = ({ priority }) => {
        const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.P2;
        const isYellow = cfg.color === '#FFD600';
        const badgeColor = (theme === 'light' && isYellow) ? '#111' : cfg.color;
        const badgeBg    = (theme === 'light' && isYellow) ? '#FFD600' : cfg.bg;
        return (
            <span className="sh-priority-badge" style={{ color: badgeColor, background: badgeBg }}>
                {priority} · {cfg.label}
            </span>
        );
    };

    const StatusBadge = ({ status }) => {
        const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.open;
        const isYellow = cfg.color === '#FFD600';
        const badgeColor      = (theme === 'light' && isYellow) ? '#111' : cfg.color;
        const badgeBorderColor = (theme === 'light' && isYellow) ? '#c9a900' : `${cfg.color}33`;
        const badgeBg          = (theme === 'light' && isYellow) ? '#FFD600' : `${cfg.color}12`;
        return (
            <span className="sh-status-badge" style={{ color: badgeColor, borderColor: badgeBorderColor, background: badgeBg }}>
                {cfg.icon} {cfg.label}
            </span>
        );
    };

    const AttachmentChip = ({ att, onRemove }) => {
        const isImage = att.type?.startsWith('image/');
        return (
            <div className="sh-attachment-chip">
                {isImage ? <FiImage size={12} /> : <FiFile size={12} />}
                <span className="sh-attachment-name">{att.name}</span>
                {onRemove && (
                    <button className="sh-attachment-remove" onClick={onRemove} type="button">
                        <FiX size={10} />
                    </button>
                )}
            </div>
        );
    };

    const MessageAttachment = ({ att }) => {
        const isImage = att.type?.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp)$/i.test(att.url || '');
        if (isImage) {
            return (
                <a href={att.url} target="_blank" rel="noreferrer" className="sh-msg-attachment-img-link">
                    <img src={att.url} alt={att.name || 'attachment'} className="sh-msg-attachment-img" />
                </a>
            );
        }
        return (
            <a href={att.url} target="_blank" rel="noreferrer" className="sh-msg-attachment-file">
                <FiFile size={14} />
                <span>{att.name || 'Attachment'}</span>
            </a>
        );
    };

    // ── JSX ────────────────────────────────────────────────────────────────────

    return (
        <>
            {/* ── Create Ticket Modal ──────────────────────────────────── */}
            {showCreateModal && (
                <div className="sh-modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="sh-modal" onClick={e => e.stopPropagation()}>
                        <div className="sh-modal-header">
                            <div className="sh-modal-title">
                                <FiPlus size={18} />
                                <span>New Support Ticket</span>
                            </div>
                            <button className="sh-modal-close" onClick={() => setShowCreateModal(false)}>
                                <FiX size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleCreateTicket} className="sh-modal-body">
                            <div className="sh-form-row">
                                <div className="sh-form-group">
                                    <label className="sh-label">
                                        <FiTag size={13} /> Category
                                    </label>
                                    <select
                                        className="sh-select"
                                        value={newTicket.subject}
                                        onChange={e => setNewTicket(prev => ({ ...prev, subject: e.target.value }))}
                                    >
                                        {categories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sh-form-group">
                                    <label className="sh-label">
                                        <FiAlertCircle size={13} /> Priority
                                    </label>
                                    <select
                                        className="sh-select"
                                        value={newTicket.priority}
                                        onChange={e => setNewTicket(prev => ({ ...prev, priority: e.target.value }))}
                                    >
                                        {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                                            <option key={key} value={key}>{key} — {cfg.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {newTicket.subject === 'Other' && (
                                <div className="sh-form-group">
                                    <label className="sh-label">Custom Subject</label>
                                    <input
                                        type="text"
                                        className="sh-input"
                                        placeholder="Describe your issue in a few words…"
                                        value={newTicket.customSubject}
                                        onChange={e => setNewTicket(prev => ({ ...prev, customSubject: e.target.value }))}
                                        maxLength={120}
                                    />
                                </div>
                            )}

                            <div className="sh-form-group">
                                <label className="sh-label">
                                    <FiMessageSquare size={13} /> Message
                                    <span className="sh-char-count">
                                        {newTicket.message.length}/{MAX_MSG_LENGTH}
                                    </span>
                                </label>
                                <textarea
                                    className="sh-textarea"
                                    placeholder="Describe your issue in detail…"
                                    value={newTicket.message}
                                    onChange={e => setNewTicket(prev => ({ ...prev, message: e.target.value }))}
                                    required
                                    maxLength={MAX_MSG_LENGTH}
                                    rows={5}
                                />
                            </div>

                            {newTicket.attachments.length > 0 && (
                                <div className="sh-attachment-list">
                                    {newTicket.attachments.map((att, i) => (
                                        <AttachmentChip
                                            key={att.name || att.url || i}
                                            att={att}
                                            onRemove={() =>
                                                setNewTicket(prev => ({
                                                    ...prev,
                                                    attachments: prev.attachments.filter((_, idx) => idx !== i),
                                                }))
                                            }
                                        />
                                    ))}
                                </div>
                            )}

                            <input
                                ref={modalFileInputRef}
                                type="file"
                                multiple
                                accept={ATTACHMENT_ACCEPT}
                                style={{ display: 'none' }}
                                onChange={e => handleFileUpload(e, 'modal')}
                            />

                            <div className="sh-modal-footer">
                                <button
                                    type="button"
                                    className="sh-btn sh-btn-ghost"
                                    onClick={() => modalFileInputRef.current?.click()}
                                    disabled={isUploading}
                                >
                                    <FiPaperclip size={14} />
                                    {isUploading ? 'Uploading…' : 'Attach files'}
                                </button>
                                <div className="sh-modal-actions">
                                    <button
                                        type="button"
                                        className="sh-btn sh-btn-secondary"
                                        onClick={() => setShowCreateModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="sh-btn sh-btn-primary"
                                        disabled={isCreating || !newTicket.message.trim()}
                                    >
                                        {isCreating ? (
                                            <><span className="sh-spinner" /> Creating…</>
                                        ) : (
                                            <><FiSend size={14} /> Submit Ticket</>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── Main Layout ────────────────────────────────────────────── */}
            <div className="sh-container">

                {/* ── Left: Ticket List ─────────────────────────────────── */}
                <div className="sh-sidebar">
                    <div className="sh-sidebar-header">
                        <div className="sh-sidebar-title">
                            <FiInbox size={18} />
                            <span>Support</span>
                            {Object.values(unreadCounts).reduce((s, n) => s + n, 0) > 0 && (
                                <span className="sh-ticket-count">{Object.values(unreadCounts).reduce((s, n) => s + n, 0)}</span>
                            )}
                        </div>
                        <div className="sh-sidebar-actions">
                            <button
                                className="sh-icon-btn sh-refresh-btn"
                                onClick={fetchTickets}
                                title="Refresh tickets"
                                aria-label="Refresh tickets"
                            >
                                <span className="sh-refresh-glyph" aria-hidden="true">↻</span>
                            </button>
                            <button
                                className="sh-btn sh-btn-primary sh-btn-sm"
                                onClick={() => setShowCreateModal(true)}
                            >
                                <FiPlus size={14} /> New
                            </button>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="sh-search-wrapper">
                        <FiSearch size={14} className="sh-search-icon" />
                        <input
                            type="text"
                            className="sh-search-input"
                            placeholder="Search tickets…"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button className="sh-search-clear" onClick={() => setSearchQuery('')}>
                                <FiX size={12} />
                            </button>
                        )}
                    </div>

                    {/* Filters */}
                    <div className="sh-filters">
                        <div className="sh-filter-group">
                            <FiFilter size={12} />
                            <select
                                className="sh-filter-select"
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="open">Open</option>
                                <option value="resolved">Resolved</option>
                                <option value="closed">Closed</option>
                            </select>
                        </div>
                        <div className="sh-filter-group">
                            <select
                                className="sh-filter-select"
                                value={priorityFilter}
                                onChange={e => setPriorityFilter(e.target.value)}
                            >
                                <option value="">All Priority</option>
                                {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                                    <option key={k} value={k}>{k} — {v.label}</option>
                                ))}
                            </select>
                        </div>
                        {isSuperAdmin && (
                            <div className="sh-filter-group">
                                <select
                                    className="sh-filter-select"
                                    value={internalPriorityFilter}
                                    onChange={e => setInternalPriorityFilter(e.target.value)}
                                >
                                    <option value="">Int. Priority</option>
                                    {[1, 2, 3, 4].map(n => (
                                        <option key={n} value={n}>Level {n}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Ticket list */}
                    <div className="sh-ticket-list">
                        {isTicketsLoading ? (
                            <div className="sh-list-loading">
                                <SigningLoader />
                                <span>Loading tickets…</span>
                            </div>
                        ) : filteredTickets.length === 0 ? (
                            <div className="sh-list-empty">
                                <FiInbox size={28} />
                                <p>{searchQuery ? 'No tickets match your search' : 'No tickets yet'}</p>
                            </div>
                        ) : (
                            filteredTickets.map(ticket => {
                                const isActive = selectedTicket?.ticket_id === ticket.ticket_id;
                                const unread = unreadCounts[ticket.ticket_id] || 0;
                                const hasUnread = unread > 0 && !isActive;
                                const pCfg = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.P2;
                                const sCfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
                                return (
                                    <div
                                        key={ticket.ticket_id}
                                        className={`sh-ticket-card${isActive ? ' sh-ticket-card--active' : ''}${hasUnread ? ' sh-ticket-card--unread' : ''}`}
                                        onClick={() => handleSelectTicket(ticket)}
                                    >
                                        <div className="sh-ticket-card-header">
                                            <span className={`sh-ticket-subject${hasUnread ? ' sh-ticket-subject--unread' : ''}`} title={ticket.subject}>
                                                {ticket.subject}
                                            </span>
                                            <div className="sh-ticket-header-right">
                                                <span className="sh-ticket-time">
                                                    {formatRelativeTime(ticket.updated_at || ticket.created_at)}
                                                </span>
                                                {hasUnread && (
                                                    <span className="sh-unread-badge">
                                                        {unread > 99 ? '99+' : unread}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="sh-ticket-card-meta">
                                            {isSuperAdmin && ticket.internal_priority != null && (
                                                <span className="sh-internal-badge">
                                                    P{ticket.internal_priority}
                                                </span>
                                            )}
                                            <span className="sh-priority-dot" style={{ background: pCfg.color }} title={`${ticket.priority} — ${pCfg.label}`} />
                                            <span className="sh-ticket-priority-label" style={{ color: pCfg.color }}>
                                                {ticket.priority}
                                            </span>
                                            <span className="sh-ticket-status-dot" style={{ color: sCfg.color }}>
                                                {sCfg.icon} {sCfg.label}
                                            </span>
                                        </div>
                                        {isSuperAdmin && ticket.user_email && (
                                            <div className="sh-ticket-card-user">
                                                <FiUser size={10} />
                                                <span>{ticket.user_email}</span>
                                                {ticket.company_name && (
                                                    <>
                                                        <span className="sh-dot">·</span>
                                                        <FiBriefcase size={10} />
                                                        <span>{ticket.company_name}</span>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                        {ticket.last_message && (
                                            <p className={`sh-ticket-preview${hasUnread ? ' sh-ticket-preview--unread' : ''}`}>
                                                {ticket.last_message}
                                            </p>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* ── Right: Chat Panel ─────────────────────────────────── */}
                <div className="sh-chat-panel">
                    {!selectedTicket ? (
                        <div className="sh-empty-state">
                            <div className="sh-empty-icon">
                                <FiMessageSquare size={40} />
                            </div>
                            <h3 className="sh-empty-title">Your Support Inbox</h3>
                            <p className="sh-empty-desc">
                                Select a ticket to view the conversation, or create a new ticket to get help from our team.
                            </p>
                            <button
                                className="sh-btn sh-btn-primary"
                                onClick={() => setShowCreateModal(true)}
                            >
                                <FiPlus size={15} /> Create a Ticket
                            </button>
                        </div>
                    ) : (
                        <div className="sh-chat-view">
                            {/* Chat header */}
                            <div className="sh-chat-header">
                                <div className="sh-chat-header-left">
                                    <div className="sh-chat-title">
                                        <span className="sh-chat-subject">{selectedTicket.subject}</span>
                                        <span className="sh-chat-id">
                                            <FiHash size={10} /> {selectedTicket.ticket_id?.slice(0, 8)}
                                        </span>
                                    </div>
                                    {isSuperAdmin && (
                                        <div className="sh-chat-meta">
                                            <span className="sh-chat-meta-item">
                                                <FiUser size={11} /> {selectedTicket.user_email}
                                            </span>
                                            {selectedTicket.company_name && (
                                                <span className="sh-chat-meta-item">
                                                    <FiBriefcase size={11} /> {selectedTicket.company_name}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="sh-chat-header-right">
                                    {isSuperAdmin && (
                                        <div className="sh-internal-priority-ctrl">
                                            <span className="sh-internal-label">Int.</span>
                                            {isEditingInternal ? (
                                                <>
                                                    <select
                                                        className="sh-filter-select sh-filter-select--sm"
                                                        value={tempInternalPriority}
                                                        onChange={e => setTempInternalPriority(e.target.value)}
                                                        autoFocus
                                                    >
                                                        {[1, 2, 3, 4].map(n => (
                                                            <option key={n} value={n}>Level {n}</option>
                                                        ))}
                                                    </select>
                                                    <button className="sh-icon-btn sh-icon-btn--success" onClick={handleUpdateInternalPriority}>
                                                        <FiCheckCircle size={14} />
                                                    </button>
                                                    <button className="sh-icon-btn" onClick={() => setIsEditingInternal(false)}>
                                                        <FiX size={14} />
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    className="sh-internal-badge sh-internal-badge--btn"
                                                    onClick={() => {
                                                        setTempInternalPriority(selectedTicket.internal_priority || 4);
                                                        setIsEditingInternal(true);
                                                    }}
                                                    title="Edit internal priority"
                                                >
                                                    P{selectedTicket.internal_priority ?? '—'}
                                                    <FiEdit2 size={10} />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    <PriorityBadge priority={selectedTicket.priority} />
                                    <StatusBadge status={selectedTicket.status} />
                                    {(isSuperAdmin || selectedTicket.user_id === userId) && (
                                        <div className="sh-status-menu">
                                            {selectedTicket.status === 'open' ? (
                                                <>
                                                    <button
                                                        className="sh-btn sh-btn-secondary sh-btn-sm"
                                                        onClick={() => handleUpdateStatus('resolved')}
                                                        disabled={isUpdatingStatus}
                                                        title="Mark as resolved"
                                                    >
                                                        <FiCheckCircle size={13} /> Resolve
                                                    </button>
                                                    {isSuperAdmin && (
                                                        <button
                                                            className="sh-btn sh-btn-ghost sh-btn-sm"
                                                            onClick={() => handleUpdateStatus('closed')}
                                                            disabled={isUpdatingStatus}
                                                            title="Close ticket permanently"
                                                        >
                                                            <FiX size={13} /> Close
                                                        </button>
                                                    )}
                                                </>
                                            ) : selectedTicket.status === 'resolved' ? (
                                                <>
                                                    <button
                                                        className="sh-btn sh-btn-ghost sh-btn-sm"
                                                        onClick={() => handleUpdateStatus('open')}
                                                        disabled={isUpdatingStatus}
                                                        title="Reopen ticket"
                                                    >
                                                        <FiRefreshCw size={13} /> Reopen
                                                    </button>
                                                    {isSuperAdmin && (
                                                        <button
                                                            className="sh-btn sh-btn-ghost sh-btn-sm"
                                                            onClick={() => handleUpdateStatus('closed')}
                                                            disabled={isUpdatingStatus}
                                                            title="Close ticket permanently"
                                                        >
                                                            <FiX size={13} /> Close
                                                        </button>
                                                    )}
                                                </>
                                            ) : (
                                                /* closed */
                                                isSuperAdmin && (
                                                    <button
                                                        className="sh-btn sh-btn-ghost sh-btn-sm"
                                                        onClick={() => handleUpdateStatus('open')}
                                                        disabled={isUpdatingStatus}
                                                        title="Reopen ticket"
                                                    >
                                                        <FiRefreshCw size={13} /> Reopen
                                                    </button>
                                                )
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Messages */}
                            <div className="sh-messages">
                                {activeMessages.length === 0 && (
                                    <div className="sh-messages-empty">
                                        <FiMessageSquare size={24} />
                                        <p>No messages yet</p>
                                    </div>
                                )}
                                {activeMessages.map((msg, i) => {
                                    const isSent = msg.sender_id === userId;
                                    const initials = getInitials(msg.sender_name);
                                    return (
                                        <div key={msg.timestamp ? `${msg.timestamp}-${msg.sender_id}` : i} className={`sh-msg-row${isSent ? ' sh-msg-row--sent' : ''}`}>
                                            {!isSent && (
                                                <div className="sh-avatar" title={msg.sender_name}>
                                                    {initials}
                                                </div>
                                            )}
                                            <div className={`sh-bubble${isSent ? ' sh-bubble--sent' : ' sh-bubble--received'}`}>
                                                {!isSent && (
                                                    <span className="sh-bubble-sender">
                                                        {msg.sender_name}
                                                        {msg.sender_role === 'ADMIN' && (
                                                            <span className="sh-admin-tag">Support</span>
                                                        )}
                                                    </span>
                                                )}
                                                {msg.text && <p className="sh-bubble-text">{msg.text}</p>}
                                                {msg.attachments?.length > 0 && (
                                                    <div className="sh-msg-attachments">
                                                        {msg.attachments.map((att, j) => (
                                                            <MessageAttachment key={att.url || att.name || j} att={att} />
                                                        ))}
                                                    </div>
                                                )}
                                                <span className="sh-bubble-time">
                                                    {formatTimestamp(msg.timestamp)}
                                                </span>
                                            </div>
                                            {isSent && (
                                                <div className="sh-avatar sh-avatar--self" title={isSuperAdmin ? 'Bug Buster' : userName}>
                                                    {isSuperAdmin ? 'BB' : getInitials(userName || userEmail)}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Input */}
                            {selectedTicket.status === 'closed' ? (
                                <div className="sh-chat-closed">
                                    <FiX size={14} />
                                    This ticket is closed. Reopen it to send messages.
                                </div>
                            ) : (
                                <form className="sh-input-area" onSubmit={handleSendMessage}>
                                    {chatAttachments.length > 0 && (
                                        <div className="sh-attachment-preview">
                                            {chatAttachments.map((att, i) => (
                                                <AttachmentChip
                                                    key={att.name || att.url || i}
                                                    att={att}
                                                    onRemove={() =>
                                                        setChatAttachments(prev => prev.filter((_, idx) => idx !== i))
                                                    }
                                                />
                                            ))}
                                        </div>
                                    )}
                                    <div className="sh-input-row">
                                        <button
                                            type="button"
                                            className="sh-icon-btn sh-attach-btn"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={isUploading}
                                            title="Attach file"
                                        >
                                            {isUploading ? <span className="sh-spinner" /> : <FiPaperclip size={16} />}
                                        </button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            accept={ATTACHMENT_ACCEPT}
                                            style={{ display: 'none' }}
                                            onChange={e => handleFileUpload(e, 'chat')}
                                        />
                                        <div className="sh-input-wrapper">
                                            <textarea
                                                className="sh-msg-input"
                                                placeholder="Type a message…"
                                                value={messageInput}
                                                onChange={e => {
                                                    if (e.target.value.length <= MAX_MSG_LENGTH) {
                                                        setMessageInput(e.target.value);
                                                    }
                                                }}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleSendMessage(e);
                                                    }
                                                }}
                                                rows={1}
                                            />
                                            {messageInput.length > MAX_MSG_LENGTH * 0.8 && (
                                                <span className="sh-input-count">
                                                    {messageInput.length}/{MAX_MSG_LENGTH}
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            type="submit"
                                            className="sh-send-btn"
                                            disabled={!messageInput.trim() && chatAttachments.length === 0}
                                            title="Send (Enter)"
                                        >
                                            <FiSend size={16} />
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default SupportHub;
